"use client";

import React from "react";
import { Phone } from "lucide-react";
import { SiWhatsapp } from "@icons-pack/react-simple-icons";
import { useAtomValue, useSetAtom } from "jotai";

import Link from "@/components/link";
import { Button } from "@/components/ui/button";
import { Heading, Paragraph } from "@/components/ui/typography";
import PDPCallbackRequestedModal from "@/components/modal/pdp-callback-requested";

import { isAuthenticatedAtom, showLoginAtom } from "@/store/auth";

import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import { cn } from "@/lib/utils";

function CourseSupport({ className, position = "bottom" }) {
  const trackMoengageEvent = useMoengageEventTracker();

  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const setShowLogin = useSetAtom(showLoginAtom);

  const [isOpen, setIsOpen] = React.useState(false);

  const handleRequestCallback = () => {
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    setIsOpen(true);

    trackMoengageEvent(EVENT_NAME.BUTTON_CLICK, {
      [EVENT_ATTRIBUTE.BUTTON_NAME]: "request_call",
      [EVENT_ATTRIBUTE.SOURCE_SCREEN]: "pdp",
    });
  };

  const handleClick = (buttonName) => () => {
    trackMoengageEvent(EVENT_NAME.BUTTON_CLICK, {
      [EVENT_ATTRIBUTE.BUTTON_NAME]: buttonName,
      [EVENT_ATTRIBUTE.SOURCE_SCREEN]: "pdp",
    });
  };

  return (
    <>
      <section
        id="course-support"
        data-position={position}
        className={cn(
          "group/course-support flex flex-col p-4 md:pt-6 md:pb-5.5 md:px-8 gap-y-6",
          "rounded-lg bg-gradient-to-b from-primary-bg/30 to-grayscale-bg/30",
          className
        )}
      >
        <div className="flex flex-col gap-y-2">
          <Heading variant="h5" weight="bold">
            Need Help?
          </Heading>
          <Paragraph variant="b4">
            Get instant assistance for any payment or access issue.
            <Link
              href="tel:+917640003000"
              className="inline-block text-grayscale-header-weak font-semibold ml-2"
              onClick={handleClick("phone_no_click")}
            >
              Call 7640003000.
            </Link>
          </Paragraph>
        </div>

        <div className="flex flex-wrap gap-4">
          <Button size="small" onClick={handleRequestCallback}>
            <Phone className="fill-primary-foreground" strokeWidth={0} />
            <Paragraph variant="b4" weight="sb" className="text-inherit">
              {isAuthenticated ? "Request a Call" : "Login to Request a Call"}
            </Paragraph>
          </Button>

          <Button variant="outline" size="small" asChild>
            <Link
              href="https://wa.me/+919318480549"
              onClick={handleClick("chat_with_us")}
            >
              <span className="inline-flex items-center justify-center size-6 rounded-full bg-whatsapp">
                <SiWhatsapp className="text-white size-3.5" />
              </span>

              <Paragraph variant="b4" weight="sb" className="text-inherit">
                Chat with Us
              </Paragraph>
            </Link>
          </Button>
        </div>
      </section>

      <PDPCallbackRequestedModal open={isOpen} onOpenChange={setIsOpen} />
    </>
  );
}

export default CourseSupport;
